import React, { useEffect, useRef, useState } from 'react';
import Icon from './Icon';

// Branded replacement for the browser's alert() and confirm() boxes.
// Mount <NoticeHost /> once near the root, then call:
//   showNotice('Please enter your full name.')
//   showNotice('Something failed.', { title: 'Something went wrong', tone: 'error' })
//   if (!(await askConfirm('Delete this?', { confirmLabel: 'Delete', danger: true }))) return;

let pushNotice = null;

export function showNotice(message, opts = {}) {
  if (pushNotice) { pushNotice({ kind: 'notice', message, ...opts }); return; }
  window.alert(message); // fallback if the host is not mounted
}

export function askConfirm(message, opts = {}) {
  if (pushNotice) return new Promise(resolve => pushNotice({ kind: 'confirm', message, resolve, ...opts }));
  return Promise.resolve(window.confirm(message));
}

const TONES = {
  warn:    { icon: 'triangle-alert', key: 'c' },
  error:   { icon: 'octagon-alert',  key: 'rd' },
  info:    { icon: 'circle-check',   key: 'gn' },
  confirm: { icon: 'triangle-alert', key: 'c' },
};

export function NoticeHost({ T, serif = "'Crimson Pro', Georgia, serif", sans = "'Public Sans', 'Helvetica Neue', Arial, sans-serif" }) {
  const [queue, setQueue] = useState([]);
  const okRef = useRef(null);
  const cur = queue[0];

  useEffect(() => {
    pushNotice = n => setQueue(q => [...q, n]);
    return () => { pushNotice = null; };
  }, []);

  const close = (result) => {
    if (cur && cur.resolve) cur.resolve(result);
    setQueue(q => q.slice(1));
  };

  useEffect(() => {
    if (!cur) return;
    const t = setTimeout(() => okRef.current && okRef.current.focus(), 30);
    const onKey = e => {
      if (e.key === 'Escape') { e.preventDefault(); close(false); }
      if (e.key === 'Enter') { e.preventDefault(); close(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur]);

  if (!cur) return null;
  const C = T || {};
  const isConfirm = cur.kind === 'confirm';
  const tone = TONES[cur.tone] || (isConfirm ? TONES.confirm : TONES.warn);
  const accent = (cur.danger ? C.rd : C[tone.key]) || C.c || '#B01C24';
  const title = cur.title || (isConfirm ? 'Please confirm' : 'Please check');

  return (
    <div
      onClick={() => close(false)}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(10,8,8,0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
        animation: 'coreNoticeFade .16s ease-out',
      }}
    >
      <style>{`
        @keyframes coreNoticeFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes coreNoticeIn { from { opacity: 0; transform: translateY(8px) scale(.98) } to { opacity: 1; transform: none } }
        @media (prefers-reduced-motion: reduce) { .core-notice-card { animation: none !important } }
        .core-notice-btn:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }
      `}</style>
      <div
        className="core-notice-card"
        role={isConfirm ? 'alertdialog' : 'alert'}
        aria-modal="true"
        aria-labelledby="core-notice-title"
        aria-describedby="core-notice-msg"
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '420px', background: C.bg1 || '#fff', color: C.t0 || '#111',
          border: `1px solid ${C.b2 || 'rgba(0,0,0,0.12)'}`, borderTop: `4px solid ${accent}`, borderRadius: '14px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)', padding: '24px 24px 20px', boxSizing: 'border-box',
          animation: 'coreNoticeIn .18s ease-out',
        }}
      >
        <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
          <div style={{
            flexShrink: 0, width: '40px', height: '40px', borderRadius: '50%', background: `${accent}1A`, color: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name={tone.icon} size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div id="core-notice-title" style={{ fontFamily: serif, fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.25, color: C.t0 || '#111', marginBottom: '6px' }}>{title}</div>
            <div id="core-notice-msg" style={{ fontFamily: sans, fontSize: '14px', lineHeight: 1.6, color: C.t1 || '#333', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{cur.message}</div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '22px', flexWrap: 'wrap' }}>
          {isConfirm && (
            <button
              className="core-notice-btn"
              onClick={() => close(false)}
              style={{
                padding: '11px 18px', borderRadius: '8px', border: `1px solid ${C.b2 || 'rgba(0,0,0,0.15)'}`, background: 'transparent',
                color: C.t1 || '#333', fontFamily: sans, fontSize: '14px', fontWeight: 700, cursor: 'pointer', minWidth: '96px',
              }}
            >{cur.cancelLabel || 'Cancel'}</button>
          )}
          <button
            ref={okRef}
            className="core-notice-btn"
            onClick={() => close(true)}
            style={{
              padding: '11px 22px', borderRadius: '8px', border: 'none', background: accent, color: '#fff',
              fontFamily: sans, fontSize: '14px', fontWeight: 800, cursor: 'pointer', minWidth: '96px',
            }}
          >{isConfirm ? (cur.confirmLabel || 'Confirm') : (cur.okLabel || 'OK')}</button>
        </div>
      </div>
    </div>
  );
}